import React from 'react'
import moment from 'moment'

// components
import {
  CBadge,
  CFormInput,
  CImage,
  CSmartPagination
} from '@coreui/react-pro'
import FilterByPopover from './filters/FilterByPopover'
import RowActionMenu, { RowActionItem } from './table-action/RowActionMenu'

// icons
import ThreeDotsIconMenu from '../assets/icons/ThreeDotsOptionsIcon.png'

// helpers
import { convertMoney } from '../helpers/format-number'
import { capitalizeEachWord } from '../helpers/text-format'

// Format satu nilai field berdasarkan `type` di cardFields.
// Type yang belum dikenal dirender apa adanya supaya config tetap fleksibel.
const formatFieldValue = (item, field) => {
  const rawValue = item?.[field?.key]

  if (field?.type === 'currency') {
    return rawValue === null || rawValue === undefined || rawValue === ''
      ? '-'
      : `IDR ${convertMoney(String(rawValue))}`
  }

  if (field?.type === 'date') {
    return rawValue ? moment(rawValue).format('DD/MM/YYYY') : '-'
  }

  return rawValue === null || rawValue === undefined || rawValue === '' ? '-' : rawValue
}

const renderFieldValue = (item, field) => {
  if (field?.type === 'badge') {
    const value = item?.[field?.key]
    if (!value) return '-'
    return (
      <CBadge color={field?.badgeColors?.[value] || 'secondary'}>
        {value}
      </CBadge>
    )
  }

  return formatFieldValue(item, field)
}

// Gabungkan field `variant: 'grid'` yang berurutan jadi satu baris dua kolom.
// Field lain tetap jadi blok sendiri.
const groupCardFields = (cardFields) => {
  return cardFields.reduce((groups, field) => {
    const variant = field?.variant || 'row'
    const lastGroup = groups[groups.length - 1]

    if (variant === 'grid' && lastGroup?.variant === 'grid') {
      lastGroup.fields.push(field)
      return groups
    }

    groups.push(variant === 'grid' ? { variant, fields: [field] } : { variant, field })
    return groups
  }, [])
}

/**
 * List berbentuk card untuk tampilan mobile.
 *
 * Pemakaian:
 * <AppCardList
 *   items={projectList}
 *   cardFields={cardProjectList}
 *   titleKey='name'
 *   subtitleKey='client_name'
 *   subtitlePrefix='Client: '
 *   actions={[
 *     { label: 'Edit Project', onClick: (item) => navigate(`/project/edit/${item.id}`) },
 *     { label: 'Delete Project', onClick: (item) => setShowConfirmDelete({ show: true, type: 'project', item }) },
 *   ]}
 *   onItemClick={(item) => navigate(`/project/details/${item.id}`)}
 *   searchValue={search}
 *   onSearch={handleSearch}
 *   activePage={page}
 *   contentPerPage={perPage}
 *   contentCount={count}
 *   onPageChange={setPage}
 * />
 */
const AppCardList = ({
  items = [],
  cardFields = [],
  titleKey,
  subtitleKey,
  subtitlePrefix = '',
  secondarySubtitleKey,
  secondarySubtitlePrefix = '',
  isSecondarySubtitleHighlighted = false,
  isSubtitleHighlighted = false,
  isTitleCapitalized = false,
  actions = [],
  onItemClick,
  searchValue,
  onSearch,
  isShowFilterByPopover = false,
  columnFilter,
  setColumnFilter,
  filterSections = [],
  isShowPagination = true,
  activePage = 1,
  contentPerPage = 10,
  contentCount = 0,
  onPageChange,
  emptyText = 'No data available'
}) => {
  const fieldGroups = groupCardFields(cardFields)

  const renderGroup = (group, item, groupIndex) => {
    if (group.variant === 'grid') {
      return (
        <div className='app-card-list-grid' key={`grid-${groupIndex}`}>
          {
            group.fields.map((field) => (
              <div className='app-card-list-grid-item' key={field.key}>
                <span className='app-card-list-label'>{field.label}</span>
                <span className='app-card-list-value'>{renderFieldValue(item, field)}</span>
              </div>
            ))
          }
        </div>
      )
    }

    const field = group.field
    const value = renderFieldValue(item, field)

    if (group.variant === 'primary') {
      return (
        <div className='app-card-list-primary' key={`primary-${field.key}`}>
          <span className='app-card-list-label'>{field.label}</span>
          <span className='app-card-list-value-primary'>{value}</span>
        </div>
      )
    }

    // Label di atas nilai, rata kiri: dipakai untuk teks panjang (notes) yang
    // akan terpotong aneh kalau dipaksa satu baris kiri-kanan.
    if (group.variant === 'stacked') {
      return (
        <div className='app-card-list-stacked' key={`stacked-${field.key}`}>
          <span className='app-card-list-label'>{field.label}</span>
          <span className='app-card-list-value'>{value}</span>
        </div>
      )
    }

    if (group.variant === 'inline') {
      return (
        <div className='app-card-list-inline' key={`inline-${field.key}`}>
          <span className='app-card-list-label'>{field.label}:</span>
          <span className='app-card-list-value'>{value}</span>
        </div>
      )
    }

    return (
      <div
        className={group.variant === 'footer' ? 'app-card-list-footer' : 'app-card-list-row'}
        key={`${group.variant}-${field.key}`}
      >
        <span className='app-card-list-label'>{field.label}</span>
        <span className='app-card-list-value'>{value}</span>
      </div>
    )
  }

  return (
    <div>
      {
        onSearch && (
          <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
            <div className="app-table-search-wrapper">
              <span className="me-2">Search:</span>
              <CFormInput
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => onSearch(e?.target.value)}
                className="app-table-search"
              />
            </div>

            {
              isShowFilterByPopover && (
                <div
                  className="d-flex align-items-start"
                  style={{
                    flex: '0 0 auto',
                    marginLeft: 'auto',
                  }}
                >
                  <FilterByPopover
                    title="Filters"
                    buttonLabel="Filter by"
                    columnFilter={columnFilter}
                    setColumnFilter={setColumnFilter}
                    filterSections={filterSections}
                  />
                </div>
              )
            }
          </div>
        )
      }

      <div className='app-card-list'>
        {
          items?.length > 0 ? (
            items.map((item, index) => (
              <div
                className='app-card-list-item'
                key={item?.id ?? index}
                onClick={() => onItemClick && onItemClick(item)}
              >
                <div className='app-card-list-head'>
                  <div className='app-card-list-heading'>
                    <div className='app-card-list-title'>{isTitleCapitalized ? capitalizeEachWord(item?.[titleKey] || '-') : item?.[titleKey] || '-'}</div>
                    {
                      secondarySubtitleKey && item?.[secondarySubtitleKey] && (
                        <div className={isSecondarySubtitleHighlighted ? 'app-card-list-subtitle app-card-list-secondary-subtitle-highlight' : 'app-card-list-secondary-subtitle'}>
                          {isSecondarySubtitleHighlighted ? (
                            <>
                              <span className='app-card-list-subtitle-label'>{secondarySubtitlePrefix.trim()}</span>
                              <span className='app-card-list-subtitle-value app-card-list-subtitle-value-highlight ms-2'>{item[secondarySubtitleKey]}</span>
                            </>
                          ) : `${secondarySubtitlePrefix}${item[secondarySubtitleKey]}`}
                        </div>
                      )
                    }
                    {
                      subtitleKey && item?.[subtitleKey] && (
                        <div className='app-card-list-subtitle'>
                          <span className='app-card-list-subtitle-label'>{subtitlePrefix.trim()}</span>
                          <span className={isSubtitleHighlighted ? 'app-card-list-subtitle-value app-card-list-subtitle-value-highlight ms-2' : 'app-card-list-subtitle-value'}>{item[subtitleKey]}</span>
                        </div>
                      )
                    }
                  </div>
                  {
                    actions?.length > 0 && (
                      <div
                        className='app-card-list-actions'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RowActionMenu toggle={<CImage fluid src={ThreeDotsIconMenu} />}>
                          {
                            actions.map((action) => (
                              <RowActionItem
                                key={action.label}
                                onClick={() => action.onClick(item)}
                              >
                                {action.label}
                              </RowActionItem>
                            ))
                          }
                        </RowActionMenu>
                      </div>
                    )
                  }
                </div>

                {fieldGroups.map((group, groupIndex) => renderGroup(group, item, groupIndex))}
              </div>
            ))
          ) : (
            <div className='app-card-list-empty'>{emptyText}</div>
          )
        }
      </div>

      {
        isShowPagination && (
          <CSmartPagination
            align="center"
            activePage={activePage}
            pages={Math.ceil(contentCount / contentPerPage) || 1}
            onActivePageChange={onPageChange}
          />
        )
      }
    </div>
  )
}

export default AppCardList
